import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from './api-response.model';

/** Parámetros de query admitidos por el wrapper. */
export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  params?: QueryParams;
  headers?: Record<string, string>;
  /** Permite saltar interceptores opcionales (ej. el toast de error). */
  context?: HttpContext;
}

/**
 * Wrapper tipado sobre HttpClient.
 *
 * Centraliza la URL base (environment.api) y expone get/post/put/patch/delete
 * que devuelven Observables. Los servicios de cada feature consumirán esto en
 * la Fase B (en lugar de los arrays mock actuales).
 *
 *   this.api.get<EmpresaDto[]>('empresas')
 *   this.api.post<EmpresaDto>('empresas', body)
 */
@Injectable({ providedIn: 'root' })
export class HttpClientService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.api.replace(/\/+$/, '');

  get<T>(path: string, options?: RequestOptions): Observable<T> {
    return this.unwrap(
      this.http.get<ApiResponse<T>>(this.buildUrl(path), this.buildOptions(options)),
    );
  }

  post<T>(path: string, body?: unknown, options?: RequestOptions): Observable<T> {
    return this.unwrap(
      this.http.post<ApiResponse<T>>(this.buildUrl(path), body ?? {}, this.buildOptions(options)),
    );
  }

  put<T>(path: string, body?: unknown, options?: RequestOptions): Observable<T> {
    return this.unwrap(
      this.http.put<ApiResponse<T>>(this.buildUrl(path), body ?? {}, this.buildOptions(options)),
    );
  }

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Observable<T> {
    return this.unwrap(
      this.http.patch<ApiResponse<T>>(this.buildUrl(path), body ?? {}, this.buildOptions(options)),
    );
  }

  delete<T>(path: string, options?: RequestOptions): Observable<T> {
    return this.unwrap(
      this.http.delete<ApiResponse<T>>(this.buildUrl(path), this.buildOptions(options)),
    );
  }

  /**
   * Extrae el `data` del envelope. Los endpoints sin payload (ej. logout)
   * devuelven `data` ausente → se resuelve como `undefined`, válido para `void`.
   */
  private unwrap<T>(response$: Observable<ApiResponse<T>>): Observable<T> {
    return response$.pipe(map((res) => res.data as T));
  }

  private buildUrl(path: string): string {
    return `${this.baseUrl}/${path.replace(/^\/+/, '')}`;
  }

  private buildOptions(options?: RequestOptions) {
    let params = new HttpParams();
    if (options?.params) {
      for (const [key, value] of Object.entries(options.params)) {
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, String(value));
        }
      }
    }
    return {
      params,
      // Los `message` del backend se localizan por Accept-Language (es por defecto).
      headers: { 'Accept-Language': 'es', ...options?.headers },
      context: options?.context,
    };
  }
}
